import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PRESTA_FAQ } from "data/constants";

// Define the FAQ item interface
interface FaqItem {
  question: string;
  answer: string;
}

// Define props interface for the component
interface PrestaFaqSectionProps {
  titleLine1?: string;
  titleLine2?: string;
  faqs?: FaqItem[];
  bgGradient?: string;
  titleColor?: string;
  questionColor?: string;
  answerColor?: string;
  toggleBgColor?: string;
  toggleTextColor?: string;
  bg?: string;
}

const CallToFaqSection: React.FC<PrestaFaqSectionProps> = ({
  titleLine1 = "QUESTIONS",
  titleLine2 = "FRÉQUENTES",
  faqs = PRESTA_FAQ,
  bgGradient = "from-gray-50 to-gray-200",
  titleColor = "text-gray-900",
  questionColor = "text-gray-800",
  answerColor = "text-gray-600",
  toggleBgColor = "bg-eventToBeePink",
  toggleTextColor = "text-white",
  bg = "bg-gradient-to-br",
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const answerVariants = {
    hidden: { opacity: 0, height: 0, transition: { duration: 0.3 } },
    visible: { opacity: 1, height: "auto", transition: { duration: 0.3 } },
  };

  return (
    <div
      className={`flex items-start justify-center p-6 ${bg} ${bgGradient} dark:bg-neutral-800 dark:from-neutral-800 `}
    >
      <div className="flex flex-col w-full gap-8 mt-20 md:flex-row max-w-7xl">
        {/* Title Section (Left) */}
        <div className="flex-1">
          <h2 className={`text-3xl dark:text-white font-extrabold ${titleColor}`}>
            <h2>{titleLine1}</h2>
            <h2>{titleLine2}</h2>
          </h2>
        </div>

        {/* Questions Section (Right) */}
        <div className="flex-1 space-y-4 pb-8">
          {faqs.map((faq, index) => (
            <div key={index} className="border-b border-gray-200 relative z-10">
              <div
                className="flex items-center justify-between py-4 cursor-pointer"
                onClick={() => toggleFAQ(index)}
              >
                <h2 className={`text-lg dark:text-white font-medium ${questionColor} flex-1 pr-4`}>
                  {faq.question}
                </h2>
                <button
                  type="button"
                  aria-label={`Toggle FAQ ${index + 1}`}
                  className={`w-10 h-10 flex items-center justify-center rounded-full ${toggleBgColor} ${toggleTextColor} transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 cursor-pointer ${
                    expandedIndex === index ? "rotate-45" : ""
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleFAQ(index);
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <span className="text-xl font-bold pointer-events-none">+</span>
                </button>
              </div>
              <AnimatePresence>
                {expandedIndex === index && (
                  <motion.div
                    variants={answerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    className={`py-2 dark:text-white overflow-hidden ${answerColor}`}
                  >
                    <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CallToFaqSection;
